import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CurrencyRupeeIcon,
  SearchIcon,
  FilterIcon,
  EyeIcon,
} from "@heroicons/react/outline";
import {
  listRefundsThunk,
  approveRefundThunk,
  completeRefundThunk,
  updateRefundStatusThunk,
} from "../../../store/thunks/jewelleryPlan/cancellationThunks";
import {
  selectRefunds,
  selectRefundsPagination,
  selectRefundsLoading,
  selectRefundError,
  selectRefundActionLoading,
} from "../../../store/slices/jewelleryPlan/cancellationSlice";

const RefundManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const refunds = useSelector(selectRefunds);
  const pagination = useSelector(selectRefundsPagination);
  const loading = useSelector(selectRefundsLoading);
  const actionLoading = useSelector(selectRefundActionLoading);
  const error = useSelector(selectRefundError);

  const [filters, setFilters] = useState({
    status: "",
    branchId: "",
    page: 1,
    limit: 20,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [transactionReference, setTransactionReference] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    dispatch(listRefundsThunk(filters));
  }, [dispatch, filters]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        bg: "bg-warning-light",
        text: "text-warning-dark",
        icon: ClockIcon,
        label: "Pending",
      },
      APPROVED: {
        bg: "bg-primary-light",
        text: "text-primary-dark",
        icon: CheckCircleIcon,
        label: "Approved",
      },
      PROCESSING: {
        bg: "bg-secondary-light",
        text: "text-secondary-dark",
        icon: ClockIcon,
        label: "Processing",
      },
      COMPLETED: {
        bg: "bg-success-light",
        text: "text-success-dark",
        icon: CheckCircleIcon,
        label: "Completed",
      },
      FAILED: {
        bg: "bg-danger-light",
        text: "text-danger-dark",
        icon: XCircleIcon,
        label: "Failed",
      },
      CANCELLED: {
        bg: "bg-neutral-light",
        text: "text-neutral-dark",
        icon: XCircleIcon,
        label: "Cancelled",
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const handleApprove = async () => {
    if (!selectedRefund) return;

    try {
      await dispatch(
        approveRefundThunk({
          refundRequestId: selectedRefund._id,
          approvalData: { note },
        })
      ).unwrap();

      setShowApproveModal(false);
      setSelectedRefund(null);
      setNote("");
      dispatch(listRefundsThunk(filters));
    } catch (error) {
      alert("Failed to approve refund: " + error);
    }
  };

  const handleComplete = async () => {
    if (!selectedRefund || !transactionReference) {
      alert("Transaction reference is required");
      return;
    }

    try {
      await dispatch(
        completeRefundThunk({
          refundRequestId: selectedRefund._id,
          completionData: { transactionReference, note },
        })
      ).unwrap();

      setShowCompleteModal(false);
      setSelectedRefund(null);
      setTransactionReference("");
      setNote("");
      dispatch(listRefundsThunk(filters));
    } catch (error) {
      alert("Failed to complete refund: " + error);
    }
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const filteredRefunds = refunds.filter((refund) => {
    if (!searchTerm) return true;
    return (
      refund.requestNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      refund.planId?.planNumber
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  });

  if (loading && refunds.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Refund Management</h1>
        <p className="mt-1 text-sm text-neutral-dark">
          Manage and process refund requests
        </p>
      </div>

      {/* Filters & Search */}
      <div className="bg-background rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-dark" />
              <input
                type="text"
                placeholder="Search by request number or plan number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value, page: 1 })
            }
            className="px-4 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
          >
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-warning-light rounded-lg shadow p-4">
          <p className="text-sm text-warning-dark">Pending</p>
          <p className="text-2xl font-bold text-warning-dark">
            {refunds.filter((r) => r.status === "PENDING").length}
          </p>
        </div>
        <div className="bg-primary-light rounded-lg shadow p-4">
          <p className="text-sm text-primary-dark">Approved</p>
          <p className="text-2xl font-bold text-primary-dark">
            {refunds.filter((r) => r.status === "APPROVED").length}
          </p>
        </div>
        <div className="bg-secondary-light rounded-lg shadow p-4">
          <p className="text-sm text-secondary-dark">Processing</p>
          <p className="text-2xl font-bold text-secondary-dark">
            {refunds.filter((r) => r.status === "PROCESSING").length}
          </p>
        </div>
        <div className="bg-success-light rounded-lg shadow p-4">
          <p className="text-sm text-success-dark">Completed</p>
          <p className="text-2xl font-bold text-success-dark">
            {refunds.filter((r) => r.status === "COMPLETED").length}
          </p>
        </div>
        <div className="bg-background rounded-lg shadow p-4">
          <p className="text-sm text-neutral-dark">Total Amount</p>
          <p className="text-2xl font-bold text-text">
            {formatAmount(refunds.reduce((sum, r) => sum + (r.amount || 0), 0))}
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-danger-light border border-danger rounded-lg p-4">
          <div className="flex items-start">
            <XCircleIcon className="h-5 w-5 text-danger mt-0.5 mr-2" />
            <div>
              <p className="text-sm font-medium text-danger-dark">Error</p>
              <p className="text-sm text-danger-dark mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Refunds List */}
      <div className="bg-background rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-light">
            <thead className="bg-neutral-light">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Request Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Requested
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-neutral-light">
              {filteredRefunds.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <CurrencyRupeeIcon className="h-12 w-12 text-neutral mx-auto mb-2" />
                    <p className="text-neutral-dark">No refunds found</p>
                  </td>
                </tr>
              ) : (
                filteredRefunds.map((refund) => (
                  <tr
                    key={refund._id}
                    className="hover:bg-neutral-light transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-text">
                          {refund.requestNumber}
                        </p>
                        <p className="text-xs text-neutral-dark">
                          {refund.reason}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-text">
                        {refund.planId?.planNumber || "N/A"}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-bold text-text">
                        {formatAmount(refund.amount)}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-text">{refund.method}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(refund.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-xs text-neutral-dark">
                        {formatDate(refund.requestedAt)}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button
                          onClick={() =>
                            navigate(
                              `/jewellery/cancellations/refunds/${refund._id}`
                            )
                          }
                          className="text-primary hover:text-primary-dark"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {refund.status === "PENDING" && (
                          <button
                            onClick={() => {
                              setSelectedRefund(refund);
                              setShowApproveModal(true);
                            }}
                            className="px-3 py-1 bg-success text-white rounded hover:bg-success-dark text-xs"
                          >
                            Approve
                          </button>
                        )}
                        {(refund.status === "APPROVED" ||
                          refund.status === "PROCESSING") && (
                          <button
                            onClick={() => {
                              setSelectedRefund(refund);
                              setShowCompleteModal(true);
                            }}
                            className="px-3 py-1 bg-primary text-white rounded hover:bg-primary-dark text-xs"
                          >
                            Complete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-light">
            <div className="flex items-center justify-between">
              <div className="text-sm text-neutral-dark">
                Showing {(pagination.page - 1) * filters.limit + 1} to{" "}
                {Math.min(pagination.page * filters.limit, pagination.total)} of{" "}
                {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-neutral-light rounded hover:bg-neutral-light disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 border border-neutral-light rounded hover:bg-neutral-light disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Approve Modal */}
      {showApproveModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Approve Refund
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Request Number</p>
              <p className="font-medium">{selectedRefund.requestNumber}</p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Amount</p>
              <p className="text-lg font-bold text-text">
                {formatAmount(selectedRefund.amount)}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Approval Note (Optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Add any notes..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setSelectedRefund(null);
                  setNote("");
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success-dark disabled:opacity-50"
              >
                {actionLoading ? "Approving..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Modal */}
      {showCompleteModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Complete Refund
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Request Number</p>
              <p className="font-medium">{selectedRefund.requestNumber}</p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Amount</p>
              <p className="text-lg font-bold text-text">
                {formatAmount(selectedRefund.amount)}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Transaction Reference *
              </label>
              <input
                type="text"
                value={transactionReference}
                onChange={(e) => setTransactionReference(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Enter transaction reference..."
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Completion Note (Optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Add any notes..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setSelectedRefund(null);
                  setTransactionReference("");
                  setNote("");
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={actionLoading || !transactionReference}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              >
                {actionLoading ? "Completing..." : "Mark as Completed"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundManagement;
